import { useEffect } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { type OrderDetails } from '@koala/sdk';
import { StyledLoyaltyContainer } from '../../styles';
import { ConnectedStaticHeartIcon } from '../../uielements';
import { FavoriteOrderCard } from '@/components/account/favorites/favoriteOrder';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { ConnectionErrorState } from '@/components/connectionError';
import GenericErrorBoundary from '@/components/genericErrorBoundary';
import {
  StyledFavoritesFlexContainer,
  StyledNoDataContainer,
  StyledNoDataContent,
} from '@/components/uielements/favoriteCard/styles';
import { CSS_CLASSES } from '@/constants/cssClassNames';
import basketActions from '@/redux/basket/actions';
import meActions from '@/redux/me/actions';
import { type RootState } from '@/types/app';

type Props = ReduxProps;

const CONNECTION_ERROR_TEST_ID = 'favorite-orders-connection-error';

const FavoriteOrders = ({
  fetchMeFavoriteOrders,
  meFavoriteOrders,
  meFavoriteOrdersConnectionError,
  meFavoriteOrdersLoading,
  reorderBasket,
  fetchMeOrderDetailsSuccess,
  reorderBasketId,
}: Props) => {
  useEffect(() => {
    fetchMeFavoriteOrders();
  }, [fetchMeFavoriteOrders]);

  const handleRetry = () => fetchMeFavoriteOrders();

  function handleReorder(orderDetails: OrderDetails) {
    fetchMeOrderDetailsSuccess(orderDetails);
    reorderBasket(orderDetails, 'Favorites', true);
  }

  // A network-layer failure used to fall through to the "no favorite orders yet" empty
  // state below, so a dropped connection looked identical to having no favorites
  // (TEAM-14780). With nothing cached to preserve, the panel replaces that empty state.
  if (meFavoriteOrdersConnectionError && !meFavoriteOrders?.length) {
    return (
      <StyledLoyaltyContainer>
        <StyledFavoritesFlexContainer className={CSS_CLASSES.LOYALTY.PAGE_HEADER}>
          <StringAccessor tag="h2" accessor="favorites.orders_header" html={true} />
          <ConnectionErrorState data-testid={CONNECTION_ERROR_TEST_ID} onRetry={handleRetry} />
        </StyledFavoritesFlexContainer>
      </StyledLoyaltyContainer>
    );
  }

  // Renders while a fetch is in flight with nothing cached. Without this the empty
  // state showed during every load — including the retry from the panel above, which
  // flashed "no favorite orders yet" for the whole request and put the diner back in
  // front of the ambiguity this ticket removes.
  if (meFavoriteOrdersLoading && !meFavoriteOrders?.length) {
    return null;
  }

  if (!meFavoriteOrders?.length) {
    return (
      <StyledLoyaltyContainer>
        <StyledFavoritesFlexContainer className={CSS_CLASSES.LOYALTY.PAGE_HEADER}>
          <StringAccessor tag="h2" accessor="favorites.orders_header" html={true} />
          <StyledNoDataContainer>
            <StyledNoDataContent>
              <ConnectedStaticHeartIcon />
              <StringAccessor tag="h3" accessor="favorites.orders_empty_state_header" html={true} />
              <StringAccessor
                tag="p"
                accessor="favorites.orders_empty_state_subheader"
                html={true}
              />
            </StyledNoDataContent>
          </StyledNoDataContainer>
        </StyledFavoritesFlexContainer>
      </StyledLoyaltyContainer>
    );
  }

  return (
    <StyledLoyaltyContainer className={CSS_CLASSES.LOYALTY.PAGE_HEADER}>
      <StyledFavoritesFlexContainer>
        <StringAccessor tag="h2" accessor="favorites.orders_header" html={true} />
        {/* A refetch that drops while a previously-loaded list is on screen keeps the
            last-known-good favorites and flags the failure above them, rather than
            replacing usable content — same treatment the receipt gets on
            order-confirmation (TEAM-14742 AC4). */}
        {meFavoriteOrdersConnectionError && (
          <ConnectionErrorState
            variant="banner"
            data-testid={CONNECTION_ERROR_TEST_ID}
            onRetry={handleRetry}
          />
        )}
        {meFavoriteOrders.map((order: OrderDetails, index: number) => (
          <GenericErrorBoundary key={index}>
            <FavoriteOrderCard
              showSubtotal
              order={order}
              index={index}
              reorderBasketId={reorderBasketId}
              reorderBasket={handleReorder}
            />
          </GenericErrorBoundary>
        ))}
      </StyledFavoritesFlexContainer>
    </StyledLoyaltyContainer>
  );
};

const mapStateToProps = (state: RootState) => ({
  meFavoriteOrders: state.app.me.favoriteOrders,
  meFavoriteOrdersConnectionError: state.app.me.favoriteOrdersConnectionError,
  meFavoriteOrdersLoading: state.app.me.favoriteOrdersLoading,
  reorderBasketId: state.app.basket.reorderBasketId,
});

const mapDispatchToProps = {
  fetchMeFavoriteOrders: meActions.fetchMeFavoriteOrders,
  fetchMeOrderDetailsSuccess: meActions.fetchMeOrderDetailsSuccess,
  reorderBasket: basketActions.reorderBasket,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(FavoriteOrders);
